import { AlertTriangle, CheckCircle2, Cpu, RefreshCw } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Badge } from "./ui/badge";
import { useFirmware } from "@/contexts/FirmwareContext";
import {
  TRATATIVAS,
  VERSAO_MINIMA_TRATATIVAS,
  tratativaAtiva,
} from "@/lib/tratativasImpressao";
import type { ImpressaoConfigResponse } from "@/types/impressao-config-response";
import { useEffect } from "react";

type Props = {
  impressaoConfig: ImpressaoConfigResponse;
  newImpressaoConfig: ImpressaoConfigResponse | null;
  setNewImpressaoConfig: React.Dispatch<
    React.SetStateAction<ImpressaoConfigResponse | null>
  >;
  isLoading: boolean;
};

/**
 * Liga/desliga as tratativas de gravação do módulo.
 *
 * Cada chave só fica editável se o firmware conectado anunciar que sabe
 * controlá-la (campo `CAPTRAT>` na resposta do ST). Em firmware antigo os
 * seletores ficam travados — lá as tratativas rodam sempre, e desligar no
 * banco não teria efeito nenhum, o que seria pior que travar.
 */
export function TratativasImpressaoSection({
  impressaoConfig,
  newImpressaoConfig,
  setNewImpressaoConfig,
  isLoading,
}: Props) {
  const {
    firmware,
    vindoDoCache,
    moduloRespondeu,
    atualizarInfoFirmware,
    suportaTratativa,
  } = useFirmware();

  // Abrir preferências já pede um status fresco ao módulo.
  useEffect(() => {
    atualizarInfoFirmware();
  }, [atualizarInfoFirmware]);

  const configAtual = newImpressaoConfig ?? impressaoConfig;
  const temSuporte = (firmware?.capacidades.length ?? 0) > 0;

  return (
    <Card className="col-span-2 md:col-span-4">
      <CardContent className="grid gap-4 pt-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Cpu className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">Tratativas de gravação</span>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={atualizarInfoFirmware}
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Verificar módulo
          </Button>
        </div>

        <p className="text-xs text-muted-foreground">
          Verificações extras que o módulo faz em volta da gravação. Desligar
          uma delas deixa a impressão mais rápida e, na mesma medida, deixa
          passar o problema que ela cobria. Na dúvida, mantenha tudo ligado — é
          como o módulo sempre funcionou.
        </p>

        {temSuporte ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
            <span>
              Módulo com firmware <strong>{firmware?.versao || "?"}</strong>
              {vindoDoCache && " (última leitura conhecida)"}.
            </span>
          </div>
        ) : (
          <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-xs">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
            <span>
              {moduloRespondeu || firmware ? (
                <>
                  O módulo conectado está no firmware{" "}
                  <strong>{firmware?.versao || "antigo"}</strong>, que executa
                  todas as tratativas de forma fixa. Atualize para a versão{" "}
                  <strong>{VERSAO_MINIMA_TRATATIVAS}</strong> ou superior para
                  poder escolher.
                </>
              ) : (
                <>
                  Módulo não respondeu. Ligue o módulo de impressão e clique em
                  “Verificar módulo” para liberar as opções (requer firmware{" "}
                  <strong>{VERSAO_MINIMA_TRATATIVAS}</strong> ou superior).
                </>
              )}
            </span>
          </div>
        )}

        <div className="grid gap-3 md:grid-cols-2">
          {TRATATIVAS.map((tratativa) => {
            const suportada = suportaTratativa(tratativa.chave);
            const ligada = tratativaAtiva(configAtual, tratativa.campo);
            const id = `tratativa-${tratativa.chave}`;
            return (
              <div
                key={tratativa.chave}
                className={`flex items-start justify-between gap-3 rounded-md border p-3 ${
                  suportada ? "" : "opacity-60"
                }`}
              >
                <div className="grid gap-1">
                  <Label htmlFor={id} className="text-sm">
                    {tratativa.titulo}
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {tratativa.descricao}
                  </p>
                  {!suportada && (
                    <Badge variant="outline" className="w-fit text-[10px]">
                      Requer firmware {VERSAO_MINIMA_TRATATIVAS}+
                    </Badge>
                  )}
                </div>
                <Switch
                  id={id}
                  checked={ligada}
                  disabled={isLoading || !suportada}
                  onCheckedChange={(valor) => {
                    setNewImpressaoConfig((anterior) =>
                      anterior
                        ? { ...anterior, [tratativa.campo]: valor }
                        : anterior,
                    );
                  }}
                />
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
